import React, {useState, useRef, useCallback} from 'react';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {Box, Link, Typography} from '@mui/material';
import {t} from 'i18next';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {ReactComponent as Save} from '../../../../assets/images/save.svg';
import {ReactComponent as Upload} from '../../../../assets/images/upload.svg';
import './CommunicationsExplorerHeader.scss';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import {
  ToastType as SharedToastType,
} from '../../../../shared/constants';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import SaveCommunicationPanel from './save-communication-report-panel/SaveCommunicationPanel';
import {ReportPayload} from '../FilterHeader';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {useNavigate} from 'react-router-dom';
import { COMMUNICATION_EXPLORER_TOOLTIP_URL } from '../../../../config/env';

interface CommunicationsExplorerHeaderProps {
  hasSelectedSources: boolean;
  reportCriteria: ReportPayload | null;
  dataAsOf: string;
  reportId?: string | number | null;
  onShareReport: () => void;
  reportTitle: string;
}

// ---------------- TOAST HOOK ----------------
const useToast = () => {
  const [toasts, setToasts] = useState<
    {id: number; message: string; type: SharedToastType}[]
  >([]);
  const nextId = useRef(0);

  const addToast = useCallback(
    (message: string, type: SharedToastType = 'info' as SharedToastType) => {
      const id = nextId.current++;
      setToasts((p) => [...p, {id, message, type}]);
    },
    []
  );

  const removeToast = useCallback((id: number) => {
    setToasts((p) => p.filter((t) => t.id !== id));
  }, []);

  return {toasts, addToast, removeToast};
};

// ---------------- COMPONENT ----------------

const CommunicationsExplorerHeader: React.FC<
  CommunicationsExplorerHeaderProps
> = ({
  hasSelectedSources,
  reportCriteria,
  dataAsOf,
  reportId,
  onShareReport,
  reportTitle,
}) => {
  const [isEditViewOpen, setIsEditViewOpen] = useState(false);
  const {toasts, addToast, removeToast} = useToast();

  // ---------------- HANDLERS ----------------
  const handleSaveClick = useCallback(() => {
    if (!hasSelectedSources) {
      addToast(
        t('communicationExplorer.header.save.noSourceSelectedError'),
        'error' as SharedToastType
      );
      return;
    }
    setIsEditViewOpen(true);
  }, [hasSelectedSources, addToast]);
  const {backSourceLabel, backSourcePath} = useSelector(
    (state: RootState) => state.comExplorer
  );
  const navigate = useNavigate();
  const uId = reportId || 'default';
  const backButtonText = backSourceLabel
    ? t('reports.dataAnalysisReport.conceptExplorer.backButtonToSource', {
        source: backSourceLabel,
      })
    : t('reports.dataAnalysisReport.conceptExplorer.backButton');

  const handleBackNavigation = () => {
    if (backSourcePath) navigate(backSourcePath);
    else navigate(-1);
  };
  return (
    <>
      <Box className="view-commnExplorer-header" data-testid="ceh-header">
        <Box
          className="view-commnExplorer-title-wrapper"
          data-testid="ceh-title-wrapper"
        >
          <Box className="view-commnExplorer-title-container">
            <Box
              className="view-commnExplorer-title"
              data-testid="ceh-title-container"
            >
              <Typography
                className="typo-text headline3_size view-commnExplorer-title-text"
                data-testid="ceh-title-text"
                tabIndex={0}
              >
                {reportTitle && reportTitle.trim().length > 0
                  ? reportTitle
                  : t('communicationExplorer.header.title')}{' '}
              </Typography>

              <ArrTooltipWrapper
                data-testid="ceh-tooltip-wrapper"
                title={t('communicationExplorer.header.headerTooltip.title')}
                trigger={
                  <div tabIndex={0} data-testid={`communications-explorer-header-div`}>
                    <InfoIcon
                      className="view-user-info-icon"
                      data-testid={`ceh-info-icon-${uId}`}
                    />
                  </div>
                }
              >
                <div data-testid={`ceh-tooltip-content-${uId}`} tabIndex={0}>
                  <p data-testid={`communications-explorer-header-content-domain-url`}>
                    {t('communicationExplorer.header.headerTooltip.content')}
                    <Link
                      href={COMMUNICATION_EXPLORER_TOOLTIP_URL}
                      target="_blank"
                    >
                      {t(
                        'communicationExplorer.header.headerTooltip.domainUrl'
                      )}
                    </Link>
                  </p>
                  <p data-testid={`communications-explorer-header-content1`}>
                    {t('communicationExplorer.header.headerTooltip.content1')}
                  </p>
                  <p data-testid={`communications-explorer-header-content2`}>
                    {t('communicationExplorer.header.headerTooltip.content2')}
                  </p>
                  <p data-testid={`communications-explorer-header-content3`}>
                    {t('communicationExplorer.header.headerTooltip.content3')}
                  </p>
                  <p data-testid={`communications-explorer-header-content4`}>
                    {t('communicationExplorer.header.headerTooltip.content4')}
                  </p>
                </div>
              </ArrTooltipWrapper>
            </Box>
            {backSourcePath && (
              <Box>
                <ArrButton
                  variant="text"
                  key="back to source name"
                  size="small"
                  onClick={handleBackNavigation}
                  dataTestId="communications-explorer-header-button">
                  <ArrowBackIcon fontSize="small" />
                  {backButtonText}
                </ArrButton>
              </Box>
            )}
          </Box>

          <Box className="view-commnExplorer-actions" data-testid="ceh-actions">
            <ArrIconButton
              icon={<Upload />}
              onClick={onShareReport}
              testId="ceh-upload-button"
              ariaLabel={t('common.export', 'Export')}
              shape="default"
              size="large"
              tooltipContent={t('common.export', 'Export')}
            />
            <ArrIconButton
              icon={<Save />}
              onClick={handleSaveClick}
              testId="ceh-save-button"
              ariaLabel={t('common.save', 'Save')}
              shape="default"
              size="large"
              tooltipContent={t('common.save', 'Save')}
            />
          </Box>
        </Box>
      </Box>
      {/* Toast */}
      <Box
        className="toast-notification-container"
        data-testid="ceh-toast-container"
      >
        {toasts.map((toast) => (
          <ArrToastNotification
            key={toast.id}
            data-testid={`ceh-toast-${toast.id}`}
            message={toast.message}
            status={[toast.type]}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </Box>
      {/* Save Panel */}
      {isEditViewOpen && (
        <SaveCommunicationPanel
          isOpen={isEditViewOpen}
          onClose={() => setIsEditViewOpen(false)}
          reportCriteria={reportCriteria}
          dataAsOf={dataAsOf}
          reportId={reportId ?? null}
          data-testid="ceh-save-panel"
        />
      )}
    </>
  );
};

export default CommunicationsExplorerHeader;
